import type { PlaceMatch } from '@chukua/common';
import { MapPin, Store } from 'lucide-react';
import type { ReactNode } from 'react';
import type { PlaceSearchState } from './use-place-search';
import type { ZoneListItem } from '../zones.api';

export function OptionLabel({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <span className="flex items-start gap-2 py-0.5">
      <span className="mt-0.5 shrink-0 text-muted">{icon}</span>
      <span className="min-w-0">
        <span className="block truncate text-fg">{title}</span>
        {detail && <span className="block truncate text-xs text-muted">{detail}</span>}
      </span>
    </span>
  );
}

/** What was picked from a place search's list: an existing zone, or a place from the geocoder. */
export type PlaceSearchPick = { type: 'zone'; zoneId: string } | { type: 'place'; place: PlaceMatch } | null;

/** The grouped options both zone search boxes show: existing zones first, so a duplicate is obvious, then places. */
export function placeSearchOptions(state: PlaceSearchState) {
  if (state.status !== 'found') return [];
  const zoneOption = (zone: ZoneListItem) => ({
    value: `zone:${zone.id}`,
    label: <OptionLabel icon={<MapPin className="size-4" />} title={zone.name} detail={`Existing zone · ${zone.kindName ?? zone.kind}`} />,
  });
  const placeOption = (place: PlaceMatch) => ({
    value: `place:${place.id}`,
    label: (
      <OptionLabel icon={place.kind === 'business' ? <Store className="size-4" /> : <MapPin className="size-4" />} title={place.name} detail={place.context} />
    ),
  });
  return [
    ...(state.zones.length ? [{ label: 'Existing zones', options: state.zones.map(zoneOption) }] : []),
    ...(state.places?.length ? [{ label: 'Places', options: state.places.map(placeOption) }] : []),
  ];
}

/** Reads an option's value back into what it stands for. */
export function placeSearchPick(value: string, state: PlaceSearchState): PlaceSearchPick {
  if (value.startsWith('zone:')) return { type: 'zone', zoneId: value.slice('zone:'.length) };
  if (state.status !== 'found') return null;
  const place = state.places?.find((candidate) => `place:${candidate.id}` === value);
  return place ? { type: 'place', place } : null;
}

/**
 * What the list says when it has nothing to pick. `fallback` says what to do instead, where that differs:
 * the new-zone box suggests drawing the zone on the map.
 */
export function placeSearchEmptyMessage(state: PlaceSearchState, fallback = ''): ReactNode {
  const message = (text: string) => <span className="text-muted">{[text, fallback].filter(Boolean).join(' ')}</span>;
  if (state.status === 'searching') return message('Searching…');
  if (state.status === 'failed') return message('Couldn’t search just now.');
  if (state.status !== 'found') return null;
  if (state.places === null && state.zones.length === 0) return message('Place search isn’t available.');
  return message('No places match. Try another name.');
}
